// Het statusoverzicht in Beheer › Plannen (pilot 26-10, baan 2P; sinds blok 8 onder "Alle wijzigingen"): per propositie het plan van de AM
// en de BP naast elkaar.
//
// Wat beheer hier ziet, rechtstreeks van de server (GET /api/beheer/plannen,
// contract api/beheer-plannen.schema.json); dit scherm rekent niets:
//  · per rol wie keuzes heeft, met de voortgang in 1.1–2.3;
//  · wie van die rol nog niet begon;
//  · de laatste 30 wijzigingen (wie, paragraaf, wat, wanneer);
//  · het label "test" voor een testaccount en "beheer als AM/BP" voor wat
//    beheer via "Bekijk als" schreef.
//
// "Testkeuzes wissen" wist per plan ALLEEN de rijen van testaccounts en van
// "beheer als AM/BP", zodat een echte AM later een schoon plan krijgt. Het
// schrijft in de gedeelde database, dus pas na een bevestiging, en daarna
// haalt het vlak het overzicht opnieuw op.

import { useState } from "react";

import { LoadError } from "../../components/Empty";
import { Card } from "../../components/ui";
import type {
  PlannenKant,
  PlannenLabel,
  PlannenLogRegel,
  PlannenParVoortgang,
  PlannenPersoon,
  PlannenPlan,
} from "../../contracts";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import { color, radius, sectionLabel, size, weight } from "../../tokens";

const STAP_WOORD: Record<string, string> = {
  alleen: "bezig",
  uitslag: "uitslag",
  tekst: "tekst",
  klaar: "klaar",
};

const PARAGRAAF_WOORD = (p: string) => (p === "lijst" ? "lijst van 30" : p);

function tijd(op: string | null): string {
  if (!op) return "";
  return new Date(op).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Hoeveel rijen "testkeuzes wissen" zou raken: testaccounts en beheer-als. */
function testRijen(plan: PlannenPlan): number {
  return plan.kanten
    .flatMap((k) => k.personen)
    .filter((p) => p.isTest || p.namens)
    .reduce((n, p) => n + Object.values(p.voortgang).filter(Boolean).length, 0);
}

export function PlannenStatus() {
  const res = useResource(() => api.beheerPlannen(), []);
  const [keuze, setKeuze] = useState<string | null>(null);
  if (res.error) return <LoadError reason={res.error} onRetry={res.reload} />;
  if (!res.data) return <p style={{ color: color.faint, fontSize: size.meta }}>Laden…</p>;
  const plannen = res.data.plannen;
  if (!plannen.length) {
    return (
      <p style={{ color: color.soft, fontSize: size.body }}>
        Er is nog geen pilotpropositie met een plan.
      </p>
    );
  }
  const sleutel = (p: PlannenPlan) => `${p.propositie}-${p.jaar}`;
  // Standaard het eerste plan waar al iemand in werkt; anders het eerste.
  const plan =
    plannen.find((p) => sleutel(p) === keuze) ??
    plannen.find((p) => p.kanten.some((k) => k.personen.length > 0)) ??
    plannen[0];
  if (!plan) return null;
  const dubbelJaar = new Set(plannen.map((p) => p.propositie)).size < plannen.length;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
      <div role="tablist" aria-label="Plannen per propositie" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {plannen.map((p) => {
          const actief = sleutel(p) === sleutel(plan);
          const mensen = p.kanten.reduce((n, k) => n + k.personen.length, 0);
          return (
            <button
              key={sleutel(p)}
              type="button"
              role="tab"
              aria-selected={actief}
              onClick={() => setKeuze(sleutel(p))}
              style={{
                border: `1px solid ${actief ? color.ink : color.border}`,
                background: actief ? color.ink : "transparent",
                color: actief ? color.surface : color.body,
                borderRadius: radius.pill,
                padding: "6px 12px",
                fontSize: size.meta,
                cursor: "pointer",
                maxWidth: "100%",
                textAlign: "left",
              }}
            >
              {p.naam}
              {dubbelJaar ? ` ${p.jaar}` : ""}
              <span style={{ opacity: 0.7 }}> · {mensen ? `${mensen} pers.` : "nog leeg"}</span>
            </button>
          );
        })}
      </div>
      <PlanKaart key={sleutel(plan)} plan={plan} herlaad={res.reload} />
    </div>
  );
}

function PlanKaart({ plan, herlaad }: { plan: PlannenPlan; herlaad: () => void }) {
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<{ fout: boolean; tekst: string } | null>(null);
  const teWissen = testRijen(plan);

  const wis = async () => {
    const vraag =
      `Testkeuzes wissen in het plan van ${plan.naam} (${plan.jaar})?\n\n` +
      "Alleen de keuzes van testaccounts en van “beheer als AM/BP” gaan weg. " +
      "Echte keuzes van een AM of BP blijven staan.";
    if (!window.confirm(vraag)) return;
    setBezig(true);
    setMelding(null);
    try {
      const r = await api.testkeuzesWissen(plan.propositie, plan.jaar);
      const delen = [
        r.gewist === 1 ? "1 testkeuze gewist" : `${r.gewist} testkeuzes gewist`,
        ...(r.lijstTeruggezet ? ["de gedeelde lijst staat weer op de berekende volgorde"] : []),
      ];
      setMelding({ fout: false, tekst: `${delen.join("; ")}.` });
      herlaad();
    } catch (e) {
      setMelding({ fout: true, tekst: e instanceof Error ? e.message : "Wissen is niet gelukt." });
    } finally {
      setBezig(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
        <h2 style={{ margin: 0, fontSize: size.panelTitle, color: color.ink }}>
          {plan.naam} · {plan.jaar}
        </h2>
        <button
          type="button"
          onClick={() => void wis()}
          disabled={bezig || teWissen === 0}
          title={teWissen === 0 ? "Er staan geen testkeuzes in dit plan." : undefined}
          style={{
            border: `1px solid ${teWissen === 0 ? color.border : color.accentRed}`,
            background: "transparent",
            color: teWissen === 0 ? color.faint : color.accentRed,
            borderRadius: radius.input,
            padding: "6px 12px",
            fontSize: size.meta,
            cursor: bezig || teWissen === 0 ? "default" : "pointer",
          }}
        >
          {bezig ? "Bezig met wissen…" : "Testkeuzes wissen"}
        </button>
      </div>
      <p style={{ margin: "6px 0 14px", fontSize: size.meta, color: color.soft }}>
        {plan.heeftInhoud ? "De planinhoud staat klaar" : "De planinhoud volgt nog"} · versie {plan.versie}
        {plan.bijgewerkt ? ` · laatst bewaard ${tijd(plan.bijgewerkt)}` : " · nog niets bewaard"}
        {plan.lijstGedeeld ? " · eigen volgorde van de lijst van 30" : ""}
      </p>
      {melding && (
        <p
          role={melding.fout ? "alert" : "status"}
          style={{ margin: "0 0 14px", fontSize: size.meta, color: melding.fout ? color.accentRed : color.accentGreen }}
        >
          {melding.tekst}
        </p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
          gap: 16,
        }}
      >
        {plan.kanten.map((k) => (
          <KantBlok key={k.planrol} kant={k} />
        ))}
      </div>
      <section style={{ marginTop: 20 }}>
        <div style={sectionLabel}>Laatste wijzigingen</div>
        {plan.log.length ? (
          <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 3 }}>
            {plan.log.map((r, i) => (
              <LogRij key={`${r.wanneer}-${i}`} regel={r} />
            ))}
          </ul>
        ) : (
          <p style={{ margin: 0, fontSize: size.meta, color: color.soft }}>Nog geen wijzigingen.</p>
        )}
      </section>
    </Card>
  );
}

function KantBlok({ kant }: { kant: PlannenKant }) {
  return (
    <section
      data-kant={kant.planrol}
      style={{ border: `1px solid ${color.border}`, borderRadius: radius.block, padding: "12px 14px", minWidth: 0 }}
    >
      <div style={sectionLabel}>{kant.label}</div>
      {kant.personen.length ? (
        <div style={{ display: "grid", gap: 10 }}>
          {kant.personen.map((p) => (
            <PersoonRij key={p.gebruiker} persoon={p} />
          ))}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: size.meta, color: color.soft }}>Nog niemand begonnen.</p>
      )}
      <p style={{ margin: "10px 0 0", fontSize: size.meta, color: color.soft }}>
        {kant.nietBegonnen.length ? (
          <>
            Nog niet begonnen:{" "}
            {kant.nietBegonnen.map((w, i) => (
              <span key={w.gebruiker} data-wacht={w.gebruiker}>
                {i > 0 ? ", " : ""}
                <span style={{ color: color.body }}>{w.naam}</span>
                {w.status === "uitgenodigd" ? " (uitgenodigd)" : ""}
                {w.label ? <Etiket label={w.label} /> : null}
              </span>
            ))}
          </>
        ) : kant.personen.length ? (
          "Iedereen in deze rol is begonnen."
        ) : (
          "Nog niemand in deze rol uitgenodigd."
        )}
      </p>
    </section>
  );
}

function PersoonRij({ persoon }: { persoon: PlannenPersoon }) {
  return (
    <div data-persoon={persoon.gebruiker}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "baseline" }}>
        <span style={{ fontSize: size.body, color: color.ink, fontWeight: weight.medium }}>{persoon.naam}</span>
        {persoon.label ? <Etiket label={persoon.label} /> : null}
        <span style={{ fontSize: size.label, color: color.faint }}>
          {persoon.klaar} van 6 klaar{persoon.bijgewerkt ? ` · ${tijd(persoon.bijgewerkt)}` : ""}
        </span>
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
        {(Object.entries(persoon.voortgang) as [string, PlannenParVoortgang | null][]).map(([par, v]) => (
          <span
            key={par}
            data-par={par}
            title={v ? `${par}: ${STAP_WOORD[v.stap ?? ""] ?? "opnieuw begonnen"}${v.jijKlaar ? ", eigen keuze klaar" : ""}` : `${par}: nog niet begonnen`}
            style={{
              fontSize: size.label,
              borderRadius: radius.pill,
              padding: "2px 8px",
              border: `1px solid ${v?.stap === "klaar" ? color.accentGreen : color.border}`,
              color: v?.stap === "klaar" ? color.accentGreen : v ? color.body : color.faint,
              background: v ? color.surface : color.surfaceAlt,
            }}
          >
            {par} {v ? (STAP_WOORD[v.stap ?? ""] ?? "opnieuw") : "–"}
          </span>
        ))}
      </div>
    </div>
  );
}

function LogRij({ regel }: { regel: PlannenLogRegel }) {
  return (
    <li style={{ fontSize: size.meta, color: color.body }}>
      <span style={{ color: color.faint }}>{tijd(regel.wanneer)}</span> ·{" "}
      <span style={{ fontWeight: weight.medium }}>{regel.naam}</span>
      {regel.label ? <Etiket label={regel.label} /> : null} · {PARAGRAAF_WOORD(regel.paragraaf)} · {regel.wat}
    </li>
  );
}

function Etiket({ label }: { label: NonNullable<PlannenLabel> }) {
  const test = label === "test";
  return (
    <span
      data-label={label}
      style={{
        marginLeft: 4,
        fontSize: size.label,
        borderRadius: radius.pill,
        padding: "1px 7px",
        color: test ? color.accentAmber : color.accentPurple,
        background: test ? color.accentAmberBg : color.surfaceAlt,
        border: `1px solid ${test ? color.accentAmber : color.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}
